import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@cdn/i18n';
import { Loading, Notice } from '@cdn/ui';
import { useSession } from './context';
import type { Session } from './session';
import { Unreachable } from './session-gate';
import { SignInForm } from './signin-form';

// Follows a destination that was validated against the allowlist and rechecked against the account
function follow(session: Session, raw: string, navigate: (to: string, options: { replace: boolean }) => void) {
	const { destination } = session;
	const target = destination.permitted(
		destination.validate(raw),
		session.organizations.map(membership => membership.organization)
	);
	destination.external(target) ? window.location.assign(target) : navigate(target, { replace: true });
}

/**
 * The sign-in screen. `?return=` names where to continue; it is validated before and after signing in.
 *
 * Signed in through Beyond Accounts, a signed-out person is sent there at once, with the destination kept, and
 * comes back to it. The screen stays and asks instead after signing out, after a sign-in that came back with
 * a problem, and within a minute of the last departure, so the two services never send a browser back and
 * forth; an outage of the service is shown as such, never as a sign-in.
 *
 * Its waiting elements name the document opening's steps (`data-bui-opening-wait`, `@beyond-js/ui/opening`):
 * `session` while the session is read and, signed in already, until the destination is followed, and `signin`
 * while the browser leaves for Accounts, kept until it has left, so the opening goes on saying so instead of
 * ending on this interim line or on a sign-in form.
 */
export function SignIn() {
	const { t, translator } = useI18n();
	const session = useSession();
	const navigate = useNavigate();
	const [search] = useSearchParams();
	const requested = search.get('return') ?? session.destination.validate('/');
	// A sign-in the service could not complete comes back here with its reason, as a code of the contract
	const problem = /^[A-Z][A-Z0-9_]{2,40}$/.exec(search.get('problem') ?? '')?.[0];
	// Decided once, when the screen opens: whether this visit leaves for Beyond Accounts without asking. A
	// suspended account never leaves by itself: signing in again would not help (D26)
	const suspended = session.ending.reason === 'suspended';
	const [leaving] = useState(() => session.identity.methods.includes('platform') && !problem && !suspended && Boolean(session.departures?.due));
	const left = useRef(false);

	useEffect(() => {
		session.status === 'active' && follow(session, requested, navigate);
	}, [session, session.status, requested, navigate]);

	useEffect(() => {
		if (!leaving || left.current || session.status !== 'anonymous') return;
		left.current = true;
		session.departures?.leave();
		session.destination.remember(requested);
		void session.signin({ method: 'platform' });
	}, [leaving, session, session.status, requested]);

	// Signed in already, the screen only follows the destination: it waits in the session step until it has,
	// so the opening never hands over to a sign-in form for the one commit before the navigation
	if (session.status === 'restoring' || session.status === 'active') return <div data-bui-opening-wait="session"><Loading /></div>;
	if (session.status === 'unreachable') return <Unreachable />;
	if (leaving && session.status === 'anonymous') return <div data-bui-opening-wait="signin"><Loading label={t('identity.signin.leaving')} /></div>;
	return (
		<section aria-labelledby="signin-title" className="mx-auto w-full max-w-md py-10">
			<h1 id="signin-title" data-page-title tabIndex={-1} className="text-title-1 font-semibold leading-10 text-ink">
				{t('identity.signin.title')}
			</h1>
			<p className="mb-6 mt-2 text-muted">{t('identity.signin.lead')}</p>
			{suspended && (
				<div className="mb-6">
					<Notice tone="error" title={t('identity.ended.suspended.title')}>
						<p>{t('identity.ended.suspended.text')}</p>
					</Notice>
				</div>
			)}
			{problem && (
				<div className="mb-6">
					<Notice tone={problem === 'UPSTREAM_UNAVAILABLE' || problem === 'UNAVAILABLE' ? 'warning' : 'error'} title={t('identity.signin.failed')}>
						<p>{translator.error(problem)}</p>
					</Notice>
				</div>
			)}
			<SignInForm done={() => follow(session, requested, navigate)} leaving={() => session.destination.remember(requested)} />
		</section>
	);
}

/**
 * Where an identity provider sends the browser back. The adapter completes the exchange while the session
 * restores; then the kept destination is validated again and followed. Until it is, the waiting element names
 * the document opening's `session` step, so the opening hands over to the destination, not to this line.
 */
export function SignInReturn() {
	const session = useSession();
	const navigate = useNavigate();

	useEffect(() => {
		session.status === 'active' && follow(session, session.destination.take(), navigate);
	}, [session, session.status, navigate]);

	if (session.status === 'anonymous') return <Navigate to="/signin" replace />;
	if (session.status === 'unreachable') return <Unreachable />;
	return <div data-bui-opening-wait="session"><Loading /></div>;
}
